import React, { useState } from 'react';
import MetaTags from 'react-meta-tags';
import { Row, Col, CardBody, Card, Alert, Container } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getDatabase, ref, get } from 'firebase/database';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { useAuth } from 'hooks/useAuth';
import logo from 'assets/images/small-logo.svg';
import LanguageDropdown from 'components/Language-Dropdown';
import LabelGroup from 'components/Label-Group';
import TextInput from 'components/Text-Input';
import DisplayErrors from 'components/Display-Errors';
import PasswordRulesList from './components/Password-Rules-List';
import { SubmitButton } from './styles';

const Register = () => {
  const formConfig = {
    defaultValues: {
      email: '',
      name: '',
      lastName: '',
      code: '',
      password: '',
      confirmPassword: '',
    },
    shouldFocusError: true,
  };
  const form = useForm(formConfig);
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = form;
  const { register } = useAuth();
  const { t: translate } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const handleValidSubmit = async (data) => {
    const password = data.password;

    const isValid = [
      password.length >= 8,
      password.length <= 25,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]/.test(password),
      password === data.confirmPassword,
    ].every(Boolean);

    if (!isValid) {
      setError('password', {
        type: 'manual',
        message: translate('Invalid Password!'),
      });
      return;
    }

    try {
      setIsSending(true);
      const success = await register(data);
      setSuccess(success);
      setIsSending(false);
    } catch (error) {
      setIsSending(false);
      console.error('Registration error:', error);
      if (error === 'Firebase: Error (auth/email-already-in-use).') {
        setError('email', {
          type: 'manual',
          message: 'This email is already in use.',
        });
      } else {
        setError('submit', {
          type: 'manual',
          message: error.message,
        });
      }
    }
  };

  const handleErrors = (errors) => {
    if (!errors || Object.keys(errors).length === 0) {
      return {};
    }

    if (
      typeof errors === 'object' &&
      Object.values(errors).every((error) => typeof error === 'string')
    ) {
      return errors;
    }

    const formattedErrors = {};

    if (errors) {
      formattedErrors.required = errors.message || errors;
    }

    return formattedErrors;
  };

  const validateAccountCode = async (value, ctx, input, cb) => {
    if (value) {
      try {
        // Removes . # $ [ and ] from the string because they throw errors on firebase searches
        const unverifiedCode = value.replace(/[\.#$\[\]]/g, '');
        const db = getDatabase();
        const codeVerifierRef = ref(db, `codes/${unverifiedCode}`);

        const snapshot = await get(codeVerifierRef);
        const codeInfo = snapshot.val();

        if (!codeInfo) {
          return translate('This code is not valid!');
        }

        return true;
      } catch (error) {
        console.error('Error validating account code:', error);
        return 'Error validating code. Please try again.';
      }
    }
  };

  const password = useWatch({ name: 'password', control: form.control });
  const confirmPassword = useWatch({ name: 'confirmPassword', control: form.control });

  const passwordRules = [
    {
      label: translate('Passwords match'),
      valid: password && confirmPassword && password === confirmPassword,
    },
    {
      label: translate('At least 8 characters'),
      valid: password?.length >= 8,
    },
    {
      label: translate('One uppercase letter'),
      valid: /[A-Z]/.test(password),
    },
    {
      label: translate('One lowercase letter'),
      valid: /[a-z]/.test(password),
    },
    {
      label: translate('One number'),
      valid: /\d/.test(password),
    },
    {
      label: translate('One special character'),
      valid: /[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]/.test(password),
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>{translate('Register')} | Ectools</title>
      </MetaTags>
      <div className="home-btn d-none d-sm-block">
        <Link to="/" className="text-dark">
          <i className="fas fa-home h2" />
        </Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div style={{ backgroundColor: 'rgba(242, 242, 242, 0.22)' }}>
                  <Row>
                    <Col xs={9}>
                      <div className="text-primary p-4">
                        <h5 style={{ color: '#344792' }}>{translate('Register')}</h5>
                        <p style={{ color: '#344792' }}>{translate('Your SEO Dashboard.')}</p>
                      </div>
                    </Col>
                    <Col className="col-3" style={{ paddingLeft: '8%' }}>
                      <LanguageDropdown />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div style={{ marginBottom: '2.5rem' }}>
                    <Link to="/">
                      <img
                        src={logo}
                        alt={translate("Ectools' Logo")}
                        height="36"
                        className="ms-2"
                      />
                    </Link>
                  </div>
                  <div className="p-2">
                    <FormProvider {...form}>
                      <form className="form-horizontal" onSubmit={handleSubmit(handleValidSubmit)}>
                        {success && (
                          <>
                            <Alert
                              color="success"
                              className="d-flex align-items-center gap-2"
                              style={{ marginBottom: '2.5rem' }}
                            >
                              <i
                                className="bx bx-check-double"
                                style={{ fontSize: '1.625rem', color: 'rgba(52, 195, 143, 1)' }}
                              />
                              {translate(
                                'Registration completed. Wait for your account to be approved before you can access the dashboard.',
                              )}
                            </Alert>
                            <div className="mt-4 d-flex justify-content-center">
                              <Link to="/" className="text-primary">
                                <SubmitButton className="btn btn-block p-3" type="submit">
                                  {translate('Back to Home')}
                                </SubmitButton>
                              </Link>
                            </div>
                          </>
                        )}

                        {!success && (
                          <>
                            <Alert color="warning" style={{ marginBottom: '2.5rem' }}>
                              {translate(
                                'After registering, wait for your account to be approved.',
                              )}
                            </Alert>
                            <LabelGroup
                              column
                              mb={2}
                              htmlFor="email"
                              label="Email"
                              required
                              size={12}
                            >
                              <TextInput
                                controlName="email"
                                type="email"
                                placeholder="Enter email"
                                required
                                validation={{
                                  pattern: {
                                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                                    message: translate('Invalid email address'),
                                  },
                                }}
                              />
                              <DisplayErrors error={handleErrors(errors?.email)} />
                            </LabelGroup>

                            <LabelGroup mb={2} size={12}>
                              <Row className="w-100 m-0 p-0">
                                <Col lg={6}>
                                  <TextInput
                                    controlName="name"
                                    type="text"
                                    placeholder="Enter name"
                                    label="Name"
                                    required
                                    validation={{
                                      validate: (value) => {
                                        if (value.trim() === '')
                                          return translate('Required Field!');
                                        if (/\d/.test(value))
                                          return translate('Only letters are allowed!');
                                        return true;
                                      },
                                    }}
                                  />
                                  <DisplayErrors error={handleErrors(errors?.name)} />
                                </Col>
                                <Col lg={6}>
                                  <TextInput
                                    controlName="lastName"
                                    type="text"
                                    placeholder="Enter last name"
                                    label="LastName"
                                    required
                                    validation={{
                                      validate: (value) => {
                                        if (value.trim() === '')
                                          return translate('Required Field!');
                                        if (/\d/.test(value))
                                          return translate('Only letters are allowed!');
                                        return true;
                                      },
                                    }}
                                  />
                                  <DisplayErrors error={handleErrors(errors?.lastName)} />
                                </Col>
                              </Row>
                            </LabelGroup>

                            <LabelGroup
                              column
                              mb={2}
                              htmlFor="code"
                              label="Company Code"
                              required
                              size={12}
                            >
                              <TextInput
                                controlName="code"
                                type="text"
                                placeholder="Enter code"
                                required
                                validation={{
                                  validate: validateAccountCode,
                                }}
                              />
                              <DisplayErrors error={handleErrors(errors?.code)} />
                            </LabelGroup>

                            <LabelGroup
                              column
                              mb={2}
                              htmlFor="password"
                              label="Password"
                              required
                              size={12}
                            >
                              <TextInput
                                controlName="password"
                                type="password"
                                placeholder="Enter password"
                                required
                              />
                              <DisplayErrors error={handleErrors(errors?.password)} />
                            </LabelGroup>

                            <LabelGroup
                              column
                              mb={2}
                              htmlFor="confirmPassword"
                              label="Confirm the entered password"
                              required
                              size={12}
                            >
                              <TextInput
                                controlName="confirmPassword"
                                type="password"
                                placeholder="Confirm the entered password"
                                required
                                validation={{
                                  validate: (value) =>
                                    value === form.getValues('password') ||
                                    translate('Passwords do not match'),
                                }}
                              />
                              <DisplayErrors error={handleErrors(errors?.confirmPassword)} />
                            </LabelGroup>
                            <PasswordRulesList rules={passwordRules} isPasswordEmpty={!password} />

                            <div className="mt-5">
                              <SubmitButton
                                className="btn btn-block"
                                type="submit"
                                disabled={isSending}
                              >
                                {isSending ? (
                                  <div className="spinner-border text-light" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                ) : (
                                  translate('Register button')
                                )}
                              </SubmitButton>
                            </div>
                          </>
                        )}
                      </form>
                    </FormProvider>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  {`${translate('Already have an account?')} `}
                  <Link to="/login" className="font-weight-medium text-primary">
                    {` ${translate('Login')}`}
                  </Link>{' '}
                </p>
                <p>
                  © {new Date().getFullYear()} {translate('Ecto Digital')}.
                  {` ${translate('Created with')} `}
                  <i className="mdi mdi-heart text-danger" />
                  {` ${translate('in Curitiba')}`}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Register;
